import { Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PicklistValue } from '@/api/client';
import { useUpdateTicket } from '@/api/hooks';
import { priorityColor, statusColor } from '@/lib/status-colors';

interface Props {
  ticketId: number;
  field: 'status' | 'priority' | 'queueID' | 'issueType' | 'subIssueType';
  value: number | null;
  options: PicklistValue[];
  className?: string;
  variant?: 'badge' | 'text';
  /**
   * Extra fields merged into the PATCH body whenever the user picks a new value.
   * Use case: changing issueType also clears subIssueType in the same request.
   */
  extraFields?: Record<string, number | string | null>;
}

export function InlineEdit({ ticketId, field, value, options, className, variant = 'badge', extraFields }: Props) {
  const mutation = useUpdateTicket();
  const current = options.find((o) => Number(o.value) === value);
  const label = current?.label ?? (value == null ? '—' : `#${value}`);

  const colorClass =
    field === 'status' ? statusColor(value)
    : field === 'priority' ? priorityColor(value)
    : '';

  const onSelect = (next: number) => {
    if (next === value) return;
    const prevLabel = label;
    mutation.mutate(
      { id: ticketId, fields: { [field]: next, ...(extraFields ?? {}) } },
      {
        onSuccess: () => {
          const nextLabel = options.find((o) => Number(o.value) === next)?.label ?? `#${next}`;
          toast.success(`${field} → ${nextLabel}`, { description: `Ticket #${ticketId} (was ${prevLabel})` });
        },
        onError: (err) => {
          toast.error(`Failed to update ${field}`, { description: err instanceof Error ? err.message : 'Unknown error' });
        },
      },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'inline-flex max-w-full items-center gap-1 rounded-md text-left',
          'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          mutation.isPending && 'opacity-60',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
        disabled={mutation.isPending}
        title={label}
      >
        {variant === 'badge' ? (
          <Badge variant="secondary" className={cn('flex max-w-full items-center gap-1 border border-transparent', colorClass)}>
            {mutation.isPending ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : null}
            <span className="truncate">{label}</span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
          </Badge>
        ) : (
          <span className={cn('flex max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-sm', colorClass)}>
            {mutation.isPending ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : null}
            <span className="truncate">{label}</span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()} className="max-h-72 overflow-auto">
        {options.length === 0 && <DropdownMenuItem disabled>No options</DropdownMenuItem>}
        {options.map((o) => (
          <DropdownMenuItem key={o.value} onSelect={() => onSelect(Number(o.value))}>
            {o.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
